// src/components/Dashboard/AuditTrail.tsx
//
// The append-only audit trail, as a timeline: action, from -> to status,
// actor, comment, timestamp. Shared by the requisition and the inquiry, whose
// trails are the same shape column for column (REQUISITION_AUDIT and
// INQUIRY_AUDIT) - the only differences are which action codes appear and how
// a status renders, both of which arrive as props.
//
// Only ever populated on a single-GET detail response; the list responses omit
// it, so this must never be fed from a list query.
//
// Built with plain MUI (Box/Paper) rather than @mui/lab's Timeline - @mui/lab
// isn't a dependency of this project, so a manual left-rail timeline avoids
// adding a package for one component.
import { FunctionComponent } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { reformatDateTime } from "util/DateUtil";

// The subset of an audit row this renders. Both domains' VMs satisfy it.
export type AuditEntry = {
  id: number;
  action: string;
  from_status?: string | null;
  to_status?: string | null;
  comment?: string | null;
  actor_id?: number | null;
  actor_email?: string | null;
  created?: string | null;
};

type PropsType = {
  entries: AuditEntry[];
  /** Dot colour per action code; anything unlisted falls back to grey. */
  dotColors?: Record<string, string>;
  /** Overrides for action codes whose title-cased form reads wrong. */
  actionLabels?: Record<string, string>;
  /** How this domain renders a status string. */
  statusLabel?: (status: string | null | undefined) => string;
  emptyText?: string;
};

const DEFAULT_DOT = "#9e9e9e";

const titleCase = (action: string) =>
  action
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");

const AuditTrail: FunctionComponent<PropsType> = ({
  entries,
  dotColors = {},
  actionLabels = {},
  statusLabel = (status) => status ?? "",
  emptyText = "No audit history yet.",
}) => {
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    );
  }

  // Newest first for readability at the top of the detail view.
  const sorted = [...entries].sort((a, b) => b.id - a.id);

  return (
    <Box>
      {sorted.map((entry, index) => (
        <Box key={entry.id} sx={{ display: "flex", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: 12,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: dotColors[entry.action] ?? DEFAULT_DOT,
                flexShrink: 0,
                mt: "6px",
              }}
            />
            {index < sorted.length - 1 && (
              <Box
                sx={{
                  width: "2px",
                  flexGrow: 1,
                  background: "#e0e0e0",
                  minHeight: 24,
                }}
              />
            )}
          </Box>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, flexGrow: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                {actionLabels[entry.action] ?? titleCase(entry.action)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {entry.created ? reformatDateTime(entry.created) : "-"}
              </Typography>
            </Box>
            {(entry.from_status || entry.to_status) && (
              <Typography variant="caption" color="text.secondary" display="block">
                {statusLabel(entry.from_status) || "-"} &rarr;{" "}
                {statusLabel(entry.to_status) || "-"}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              {entry.actor_email ??
                (entry.actor_id ? `User #${entry.actor_id}` : "Unknown user")}
            </Typography>
            {entry.comment && (
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                {entry.comment}
              </Typography>
            )}
          </Paper>
        </Box>
      ))}
    </Box>
  );
};

export default AuditTrail;
